import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  Typography,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import { GridColDef } from "@mui/x-data-grid";
import { useQuery } from "@apollo/client/react";
import { GET_PASS_LIST } from "../graphql/query";
import themestyle from "../theme";
import type {
  GetPassListResponse,
  PassListItem,
} from "../interfaces/pass.interface";
import DataGrids from "./DataGrid";

type AllPassesTableProps = {
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string, name: string) => void;
  onReport: (id: string) => void;
  refreshTrigger?: number;
};

const AllPassesTable: React.FC<AllPassesTableProps> = ({
  onView,
  onEdit,
  onDelete,
  onReport,
  refreshTrigger,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [inputSearch, setInputSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });

  const { data, loading, refetch, previousData } =
    useQuery<GetPassListResponse>(GET_PASS_LIST, {
      variables: {
        page: paginationModel.page + 1,
        perPage: paginationModel.pageSize,
        search: searchQuery || "",
      },
      fetchPolicy: "cache-and-network",
    });

  useEffect(() => {
    if (refreshTrigger) refetch();
  }, [refreshTrigger, refetch]);

  // Debounce: fire backend query 400ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(inputSearch);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [inputSearch]);

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, pageSize: 5, page: 0 }));
  }, [isMobile]);

  const rows = useMemo(() => {
    const dataList: PassListItem[] = data?.getPassList?.data ?? [];
    return dataList.map((row, index) => {
      const serial =
        paginationModel.page * paginationModel.pageSize + index + 1;
      return {
        ...row,
        serial: serial.toString().padStart(2, "0"),
      };
    });
  }, [data, paginationModel.page, paginationModel.pageSize]);

  const rowCount =
    data?.getPassList?.totalCount ??
    previousData?.getPassList?.totalCount ??
    0;

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: "serial",
        headerName: "S.No",
        minWidth: 50,
        flex: 0.3,
        sortable: false,
      },
      {
        field: "passId",
        headerName: "Pass Id",
        flex: 0.7,
        minWidth: 100,
      },
      {
        field: "passName",
        headerName: "Pass Name",
        flex: 1.0,
        minWidth: 130,
      },
      {
        field: "passType",
        headerName: "Pass Type",
        flex: 0.7,
        minWidth: 100,
        renderCell: (params) => (
          <Box
            sx={{
              backgroundColor: "#E6FBEA",
              color: "#1E7F3D",
              px: 2,
              py: 0.5,
              borderRadius: "16px",
              fontSize: "14px",
              fontWeight: 500,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {params.value}
          </Box>
        ),
      },
      {
        field: "noOfClasses",
        headerName: "Classes",
        flex: 0.4,
        minWidth: 80,
        align: "center",
        headerAlign: "center",
        valueGetter: (params) => {
          const val = params.row.noOfClasses;
          return `${val} ${val === 1 ? "class" : "classes"}`;
        },
        renderCell: (params) => (
          <Typography sx={{ fontSize: "15px" }}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: "passCost",
        headerName: "Price",
        flex: 0.6,
        minWidth: 90,
        valueGetter: (params) => {
          const val = params.row.passCost;
          if (!val) return "₹0.00";
          return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
          }).format(val);
        },
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.7,
        minWidth: 100,
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              height: "100%",
              pl: 0,
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor:
                  params.value === "Active"
                    ? themestyle.colors.success
                    : themestyle.colors.error,
              }}
            />
            <Typography
              sx={{
                color:
                  params.value === "Active"
                    ? themestyle.colors.success
                    : themestyle.colors.error,
                fontSize: "14px",
                fontWeight: 600,
                fontFamily: "Roboto",
              }}
            >
              {params.value}
            </Typography>
          </Box>
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        minWidth: 100,
        flex: 0.5,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <IconButton
              size="small"
              onClick={() => onReport(params.row.id)}
              sx={{
                border: "1px solid #C7D9F5",
                color: "#1565C0",
                width: 28,
                height: 28,
              }}
            >
              <AssessmentOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onView(params.row.id)}
              sx={{
                border: "1px solid #D1E7C7",
                color: themestyle.colors.buttonGreen,
                width: 28,
                height: 28,
              }}
            >
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() =>
                onDelete(params.row.id, params.row.passName)
              }
              sx={{
                border: "1px solid #F2B6B6",
                color: "#E53935",
                width: 28,
                height: 28,
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
    ],
    [onView, onDelete, onReport]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        backgroundColor: "#F7FAF3",
        borderRadius: 3,
        width: "100%",
      }}
    >
      <Box
        sx={{
          backgroundColor: "#fff",
          borderRadius: "12px",
          border: "1px solid #E5E7EB",
          boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
          maxWidth: "100%",
        }}
      >
        <DataGrids
          rows={rows}
          columns={columns}
          toolbar
          searchQuery={inputSearch}
          onSearchQueryChange={setInputSearch}
          toolbarShowAddIcon={false}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 20]}
          getRowId={(row: any) => row.id}
          onMobileView={(row: any) => onView(row.id)}
          onMobileDelete={(row: any) => onDelete(row.id, row.passName)}
          onMobileReport={(row: any) => onReport(row.id)}
          loading={loading}
          sx={{
            height: { xs: "auto", sm: "60vh", md: "60vh" },
            minHeight: 320,
            width: "100%",
            maxWidth: "100%",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#F9FAFB",
              borderBottom: "1px solid #E5E7EB",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#F7FBF5",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #EEF2F7",
              fontSize: "13px",
              whiteSpace: "nowrap",
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default AllPassesTable;
